import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { getEntry, fileUrl } from "../lib/api";
import { generatePolaroidPicture, downloadBlob } from "../lib/picture";

export default function EntryDetail() {
  const { id } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    getEntry(id)
      .then(setEntry)
      .catch(() => setEntry(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownload = async () => {
    if (!entry) return;
    setDownloading(true);
    try {
      const { blob } = await generatePolaroidPicture(overlayRef.current);
      downloadBlob(
        blob,
        `hostbydims-${entry.guest_name.replace(/\s+/g, "_")}.jpg`,
      );
      toast.success("Picture downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate picture");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (!entry) return;
    setPrinting(true);
    try {
      const { dataUrl } = await generatePolaroidPicture(overlayRef.current);
      const win = window.open("", "_blank");
      if (!win) {
        toast.error("Please allow pop-ups to print");
        return;
      }
      const title = `hostbydims — ${entry.guest_name}`;
      win.document.write(`<!doctype html>
<html>
  <head>
    <title>${title.replace(/</g, "&lt;")}</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { margin: 0; padding: 0; background: #ffffff; }
      body {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        box-sizing: border-box;
        font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
      }
      img {
        max-width: 100%;
        max-height: 100vh;
        height: auto;
        display: block;
      }
      @media print {
        @page { margin: 12mm; }
        body { padding: 0; }
        img { max-height: none; }
      }
    </style>
  </head>
  <body>
    <img src="${dataUrl}" alt="Polaroid" onload="setTimeout(function(){window.focus();window.print();}, 150);" />
  </body>
</html>`);
      win.document.close();
    } catch (e) {
      console.error(e);
      toast.error("Could not prepare print");
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-24">
        <p className="text-[#717171]">Loading...</p>
      </section>
    );
  }

  if (!entry) {
    return (
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-24">
        <h2 className="font-heading text-3xl font-extrabold text-[#111]">
          Entry not found
        </h2>
        <Link to="/gallery" className="gb-btn-primary inline-flex items-center gap-2 mt-6">
          Back to gallery
        </Link>
      </section>
    );
  }

  return (
    <section className="px-5 pt-6 pb-24">
      <Link
        to="/gallery"
        data-testid="back-to-gallery"
        className="inline-flex items-center gap-2 text-[#8a8a8a] hover:text-[#111] text-sm"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="mt-4 text-center">
        <h1
          className="font-heading text-3xl font-extrabold text-[#111] tracking-tighter"
          data-testid="entry-guest-name"
        >
          {entry.guest_name}
        </h1>
        <p className="text-[13px] text-[#8a8a8a] mt-1.5">
          {new Date(entry.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
          {entry.guest_email && (
            <>
              {" "}
              &middot; <span>{entry.guest_email}</span>
            </>
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            data-testid="btn-download-pdf"
            onClick={handleDownload}
            disabled={downloading || printing}
            className="gb-btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Download size={14} />
            {downloading ? "Preparing..." : "Download Picture"}
          </button>
          <button
            data-testid="btn-print-picture"
            onClick={handlePrint}
            disabled={downloading || printing}
            className="gb-btn-ghost inline-flex items-center gap-2 text-sm"
          >
            <Printer size={14} />
            {printing ? "Preparing..." : "Print"}
          </button>
        </div>
      </div>

      {entry.message && (
        <p className="mt-5 text-base text-[#111] italic font-hand text-center px-6">
          &ldquo;{entry.message}&rdquo;
        </p>
      )}

      <div className="mt-8 flex justify-center">
        <div
          ref={overlayRef}
          data-testid="entry-overlay"
          className="polaroid tilt-right w-full max-w-sm"
        >
          <div className="polaroid-tape" aria-hidden="true" />
          <div className="polaroid-photo">
            <img
              src={fileUrl(entry.image_path)}
              alt="Entry"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="polaroid-caption">
            {entry.guest_name}
            <span className="sub">
              {new Date(entry.created_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
